/**
 * LINE ニュース (line_news) と関連リソースの hooks。
 * 仕様書: docs/01_spec/spec_line_bot.md A-10
 */
import type { LineNews } from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as limitFn,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';
import { safeAddDoc, safeDeleteDoc, safeUpdateDoc } from '@/lib/safeWrite';



export interface LineNewsRecord extends LineNews {
  id: string;
}

// ─────────────────────────────────────────────
// list / get
// ─────────────────────────────────────────────
export function useLineNewsList(options?: { deleted?: boolean; limit?: number }) {
  return useQuery({
    queryKey: ['line_news', options ?? null],
    queryFn: async (): Promise<LineNewsRecord[]> => {
      const snap = await getDocs(
        query(
          collection(db, 'line_news'),
          where('_deleted', '==', options?.deleted ?? false),
          orderBy('createdAt', 'desc'),
          limitFn(options?.limit ?? 100),
        ),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as LineNews) }));
    },
  });
}

export async function getLineNews(id: string): Promise<LineNewsRecord | null> {
  const snap = await getDoc(doc(db, 'line_news', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as LineNews) };
}

// ─────────────────────────────────────────────
// CRUD
// ─────────────────────────────────────────────
export function useCreateLineNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<LineNews>) => {
      const ref = await safeAddDoc(collection(db, 'line_news'), {
        title: data.title ?? '無題',
        sections: data.sections ?? [],
        templateId: data.templateId ?? '',
        formData: data.formData ?? {},
        status: data.status ?? 'draft',
        sendAt: null,
        sentAt: null,
        targetUserIds: [],
        recipientCount: null,
        sentRecipientCount: null,
        lastError: null,
        attempts: 0,
        modelUsed: data.modelUsed ?? '',
        createdBy: data.createdBy ?? '',
        _deleted: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return ref.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['line_news'] }),
  });
}

export function useUpdateLineNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; data: Partial<LineNews> }) => {
      await safeUpdateDoc(doc(db, 'line_news', args.id), {
        ...args.data,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['line_news'] }),
  });
}

/**
 * 既存ニュースを複製。タイトルに「(複製)」を付与し、status='draft' で新規作成。
 * 配信履歴系フィールド（sendAt / sentAt / *Count / lastError / attempts）はリセット。
 */
export function useDuplicateLineNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { source: LineNewsRecord; createdBy: string }): Promise<string> => {
      const { source, createdBy } = args;
      const ref = await safeAddDoc(collection(db, 'line_news'), {
        title: `${source.title || '無題'} (複製)`,
        sections: source.sections.map((s) => ({ ...s })),
        templateId: source.templateId ?? '',
        formData: source.formData ? { ...source.formData } : {},
        status: 'draft',
        sendAt: null,
        sentAt: null,
        targetUserIds: [],
        recipientCount: null,
        sentRecipientCount: null,
        lastError: null,
        attempts: 0,
        modelUsed: source.modelUsed ?? '',
        createdBy,
        _deleted: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return ref.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['line_news'] }),
  });
}

export function useSoftDeleteLineNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await safeUpdateDoc(doc(db, 'line_news', id), {
        _deleted: true,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['line_news'] }),
  });
}

export function useHardDeleteLineNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await safeDeleteDoc(doc(db, 'line_news', id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['line_news'] }),
  });
}

// ─────────────────────────────────────────────
// Callables
// ─────────────────────────────────────────────
interface GenerateNewsArgs {
  templateId?: string;
  formValues?: Record<string, string>;
  freePrompt?: string;
  model?: string;
}

const generateLineNewsFn = httpsCallable<
  GenerateNewsArgs,
  {
    ok: boolean;
    modelUsed: string;
    generated: {
      title: string;
      sections: LineNews['sections'];
    };
  }
>(functions, 'generateLineNews');

export function useGenerateLineNews() {
  return useMutation({
    mutationFn: async (args: GenerateNewsArgs) => {
      const res = await generateLineNewsFn(args);
      return res.data;
    },
  });
}

const getLineMessageQuotaFn = httpsCallable<
  Record<string, never>,
  {
    ok: boolean;
    limit: number | null;
    used: number;
    remaining: number | null;
    type: 'limited' | 'none';
  }
>(functions, 'getLineMessageQuota');

export function useLineMessageQuota() {
  return useQuery({
    queryKey: ['line_message_quota'],
    queryFn: async () => {
      const res = await getLineMessageQuotaFn({});
      return res.data;
    },
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

const scheduleLineNewsSendFn = httpsCallable<
  {
    newsId: string;
    sendAt: string;
    targetUserIds: string[];
    campaignId?: string | null;
    /** カード経由予約時の CTA 上書き URL（任意） */
    ctaUrlOverride?: string | null;
  },
  { ok: boolean; scheduleId: string; recipientCount: number }
>(functions, 'scheduleLineNewsSend');

export function useScheduleLineNewsSend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      newsId: string;
      sendAt: string;
      targetUserIds: string[];
      campaignId?: string | null;
      ctaUrlOverride?: string | null;
    }) => {
      const res = await scheduleLineNewsSendFn(args);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['line_news'] });
      qc.invalidateQueries({ queryKey: ['line_news_schedules'] });
    },
  });
}

const cancelLineNewsScheduleFn = httpsCallable<
  { scheduleId: string; newsId: string },
  { ok: boolean }
>(functions, 'cancelLineNewsSchedule');

export function useCancelLineNewsSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { scheduleId: string; newsId: string }) => {
      const res = await cancelLineNewsScheduleFn(args);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['line_news'] });
      qc.invalidateQueries({ queryKey: ['line_news_schedules'] });
    },
  });
}

/**
 * 指定 news に紐づく「アクティブな（status=scheduled）」予約を取得。
 * 1 ニュースに対し有効予約は基本 1 件想定。
 */
export interface ActiveSchedule {
  id: string;
  newsId: string;
  status: string;
}

export function useActiveScheduleForNews(newsId: string | null) {
  return useQuery({
    queryKey: ['line_news_schedules', 'active', newsId],
    enabled: !!newsId,
    queryFn: async (): Promise<ActiveSchedule | null> => {
      if (!newsId) return null;
      const snap = await getDocs(
        query(
          collection(db, 'line_news_schedules'),
          where('newsId', '==', newsId),
          where('status', '==', 'scheduled'),
          limitFn(1),
        ),
      );
      if (snap.empty) return null;
      const d = snap.docs[0];
      const data = d.data() as { newsId: string; status: string };
      return { id: d.id, ...data };
    },
  });
}

/**
 * 指定 news に紐づく「全」予約スケジュール（履歴含む）を取得。
 * 配信タブで履歴一覧表示するために使う。createdAt 降順。
 */
export interface LineNewsScheduleRecord {
  id: string;
  newsId: string;
  title: string;
  sendAt: unknown; // Timestamp
  targetUserIds: string[];
  recipientCount: number | null;
  sentRecipientCount: number | null;
  status: 'scheduled' | 'sending' | 'sent' | 'cancelled' | 'failed';
  attempts: number;
  lastError?: string | null;
  createdBy: string;
  createdAt: unknown;
  updatedAt: unknown;
}

export function useLineNewsSchedules(newsId: string | null) {
  return useQuery({
    queryKey: ['line_news_schedules', 'history', newsId],
    enabled: !!newsId,
    queryFn: async (): Promise<LineNewsScheduleRecord[]> => {
      if (!newsId) return [];
      // 注: 複合インデックス (newsId + createdAt) を firestore.indexes.json で定義済みだが、
      // 作成中（数分かかる）でも動作するよう orderBy はクライアント側でソートする。
      // 1 ニュースあたり schedule 数は実用上 〜数十件想定で、クライアント側ソートのコストは無視できる。
      const snap = await getDocs(
        query(
          collection(db, 'line_news_schedules'),
          where('newsId', '==', newsId),
          limitFn(50),
        ),
      );
      const rows = snap.docs.map(
        (d) => ({ id: d.id, ...(d.data() as Omit<LineNewsScheduleRecord, 'id'>) }),
      );
      // createdAt 降順
      const ts = (v: unknown): number => {
        if (v && typeof v === 'object' && 'toMillis' in v && typeof (v as { toMillis: unknown }).toMillis === 'function') {
          return (v as { toMillis: () => number }).toMillis();
        }
        return 0;
      };
      rows.sort((a, b) => ts(b.createdAt) - ts(a.createdAt));
      return rows;
    },
  });
}
